import { GetDocumentExtractionStatusResponse } from 'app/interfaces/responses/document';
import api from 'app/utils/api';
import apiV2 from 'app/utils/apiV2';
import type {
  AdditionalField,
  CustomSignatory,
  DocumentBaseConfig,
  FieldUpdate,
} from './document.types';

type ClassificationPagesMap = Record<string, any>;

export class DocumentService {
  public async getDocument(documentID: string) {
    return api.get(`/document/${documentID}`);
  }

  public async getDocumentExtractionStatus(
    documentIds: string[]
  ): Promise<GetDocumentExtractionStatusResponse> {
    const documentIdString = documentIds.join(',');
    return apiV2.get(`/documents/status?ids=${documentIdString}`);
  }

  public async isDocumentTypeInTransaction(
    transactionId: string,
    documentType: string
  ): Promise<{
    hasDocumentType: boolean;
    isMultiCopyType: boolean;
    count: number;
  }> {
    const response = await apiV2.get(
      `/transactions/${transactionId}/has-document-type`,
      {
        params: { documentType },
      }
    );
    return response.data;
  }
  public async getBrokerageDocuments() {
    return api.get(`/document/brokerage`);
  }

  public async getDocumentCurrentFields(
    transactionId: string,
    documentId: string
  ) {
    const response = await apiV2.get(
      `/transactions/${transactionId}/documents/${documentId}/current-fields`
    );
    return response.data;
  }

  public async updateDocumentFields(
    transactionId: string,
    documentId: string,
    fieldUpdates: FieldUpdate[],
    additionalFields?: AdditionalField[],
    customSignatories?: CustomSignatory[]
  ) {
    return apiV2.put(
      `/transactions/${transactionId}/documents/${documentId}/edit`,
      {
        fieldUpdates,
        additionalFields: additionalFields ?? [],
        customSignatories: customSignatories ?? [],
      }
    );
  }

  public async getSignatureFields(
    transactionId: string,
    documentType: string,
    options?: { signal?: AbortSignal; templateVersion?: string | null }
  ): Promise<unknown> {
    const response = await apiV2.get(
      `/transactions/${transactionId}/documents/${encodeURIComponent(documentType)}/signature-fields`,
      {
        signal: options?.signal,
        params: options?.templateVersion
          ? { templateVersion: options.templateVersion }
          : undefined,
      }
    );
    return response.data;
  }

  public async getDocumentBaseConfig(
    documentType: string,
    options?: { signal?: AbortSignal; templateVersion?: string | null }
  ): Promise<DocumentBaseConfig> {
    const response = await apiV2.get(`/documents/${documentType}/base-config`, {
      signal: options?.signal,
      params: options?.templateVersion
        ? { templateVersion: options.templateVersion }
        : undefined,
    });
    return response.data ?? {};
  }

  /**
   * Whether Max review has validation rules for a document type. The backend resolves
   * the config for the caller's partner, the same one document validation uses.
   */
  public async getDocumentValidationConfig(
    documentType: string,
    options?: { signal?: AbortSignal }
  ): Promise<{
    documentType: string;
    hasValidationConfig: boolean;
    fieldCount: number;
  }> {
    const response = await apiV2.get(
      `/documents/${documentType}/validation-config`,
      { signal: options?.signal }
    );
    return response.data;
  }

  /**
   * Update the document `type` for a document instance in a transaction.
   */
  public async updateDocumentType(
    transactionId: string,
    documentId: string,
    documentType: string,
    existingDocumentData?: any
  ): Promise<{
    message?: string;
    documentCount?: number;
    archivedCount?: number;
  }> {
    const asPagesMap = (value: any): ClassificationPagesMap | null => {
      if (!value || typeof value !== 'object' || Array.isArray(value))
        return null;
      return value as ClassificationPagesMap;
    };

    const extractPagesMap = (
      docPayload: any
    ): ClassificationPagesMap | null => {
      // Backend reclassify flow stores/reads page map under pageClassifications.<source>.pages
      // Prefer manual source, then first available source.
      const payload = docPayload?.data ?? docPayload;

      const directPages = asPagesMap(payload?.pages);
      if (directPages) return directPages;

      const pageClassifications = payload?.pageClassifications;
      if (
        pageClassifications &&
        typeof pageClassifications === 'object' &&
        !Array.isArray(pageClassifications)
      ) {
        const manualPages = asPagesMap(
          (pageClassifications as any)?.manual?.pages
        );
        if (manualPages) return manualPages;

        for (const sourceEntry of Object.values(pageClassifications)) {
          const pages = asPagesMap((sourceEntry as any)?.pages);
          if (pages) return pages;
        }
      }

      return null;
    };

    const buildReclassifyPayload = (pages: ClassificationPagesMap) => {
      const nextPages: Record<string, any> = {};
      for (const [pageNumber, pageData] of Object.entries(pages)) {
        // Ensure every page resolves to exactly one docType by overriding it.
        nextPages[String(pageNumber)] = {
          ...(pageData ?? {}),
          docType: documentType,
        };
      }
      return {
        transactionID: transactionId,
        pages: nextPages,
        ignoreDocTypes: [],
        inPlaceDocumentTypeChange: true,
      };
    };

    // Use existing in-memory classification map; do not make an extra GET.
    let pages = extractPagesMap(existingDocumentData);
    if (!pages || Object.keys(pages).length === 0) {
      const docPayload = existingDocumentData?.data ?? existingDocumentData;
      // Split children replay their page numbers onto the parent, so a stand-in page would mislabel it.
      if (docPayload?.parentDocument) {
        throw new Error(
          'This document has no page classification, so its type cannot be changed here.'
        );
      }
      // The backend prefers the document's stored classification for targetPageNumbers and only
      // falls back to these keys, so one stand-in page is enough to carry the type. Page maps are
      // 0-based (verified across prod), so page 1 here would record the document's second page.
      pages = { 0: {} };
    }
    const payload = buildReclassifyPayload(pages);
    const response = await api.post(
      `/document/${documentId}/reclassify`,
      payload
    );
    return response?.data ?? {};
  }
}
